import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, BookOpenText } from "lucide-react";

import { api, TARGET_LANGUAGES, CEFR_LEVELS, THEMES } from "@/lib/api";
import GeneratorPanel from "@/components/poc/GeneratorPanel";
import LessonView from "@/components/poc/LessonView";
import PronunciationPractice from "@/components/poc/PronunciationPractice";

export default function POC() {
    const [targetLanguage, setTargetLanguage] = useState("anglais");
    const [level, setLevel] = useState("A1");
    const [theme, setTheme] = useState("nourriture");
    const [isGenerating, setIsGenerating] = useState(false);
    const [lesson, setLesson] = useState(null);
    const [selectedSentence, setSelectedSentence] = useState(null);

    const handleGenerate = async () => {
        if (!theme.trim()) {
            toast.error("Choisissez un thème avant de générer.");
            return;
        }
        setIsGenerating(true);
        setLesson(null);
        setSelectedSentence(null);
        try {
            const { data } = await api.post("/poc/generate-lesson", {
                target_language: targetLanguage,
                native_language: "français",
                level,
                theme,
            });
            setLesson(data);
            const firstSentence = data?.vocabulary?.[0]?.natural_sentence || null;
            setSelectedSentence(firstSentence);
            toast.success("Leçon générée.");
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Erreur inconnue.";
            toast.error(`Échec de la génération : ${detail}`);
        } finally {
            setIsGenerating(false);
        }
    };

    const sentences = useMemo(() => {
        if (!lesson?.vocabulary) return [];
        return lesson.vocabulary
            .map((v) => v.natural_sentence)
            .filter((s) => typeof s === "string" && s.trim().length > 0);
    }, [lesson]);

    useEffect(() => {
        if (!selectedSentence && sentences.length > 0) {
            setSelectedSentence(sentences[0]);
        }
    }, [sentences, selectedSentence]);

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="poc-page">
            {/* Header */}
            <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <Link
                        to="/"
                        className="text-inkSoft hover:text-ink transition-colors flex items-center gap-1 text-sm"
                        data-testid="back-home-link"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Accueil</span>
                    </Link>
                    <div className="h-4 w-px bg-lexiBorder hidden sm:block" />
                    <span className="text-xl font-serif tracking-tight" data-testid="poc-brand">
                        Lexia School
                    </span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-inkSoft border border-lexiBorder rounded-full px-2 py-0.5 hidden sm:inline">
                        Démo · Phase 0
                    </span>
                </div>
                <div className="text-xs text-inkSoft flex items-center gap-2" data-testid="poc-header-status">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse-soft" />
                    3 pipelines actifs
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                {/* Intro */}
                <section className="mt-6 mb-10 max-w-4xl">
                    <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-4">
                        Preuve technique · 3 pipelines
                    </p>
                    <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="poc-title">
                        Générez, écoutez, prononcez —{" "}
                        <span className="italic text-terracotta">de bout en bout</span>.
                    </h1>
                    <p className="mt-4 text-base text-inkSoft leading-relaxed max-w-2xl">
                        Choisissez une langue, un niveau CEFR et un thème. L&apos;IA produit une
                        micro-leçon structurée, la lit à haute voix en vitesse naturelle ou
                        lente, puis analyse votre prononciation sur 5 axes.
                    </p>
                </section>

                {/* Bento grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
                    {/* Left panel */}
                    <aside className="col-span-1 lg:col-span-4">
                        <div className="lg:sticky lg:top-6">
                            <GeneratorPanel
                                targetLanguage={targetLanguage}
                                setTargetLanguage={setTargetLanguage}
                                level={level}
                                setLevel={setLevel}
                                theme={theme}
                                setTheme={setTheme}
                                onGenerate={handleGenerate}
                                isGenerating={isGenerating}
                                targetLanguages={TARGET_LANGUAGES}
                                levels={CEFR_LEVELS}
                                themes={THEMES}
                            />
                        </div>
                    </aside>

                    {/* Right panel */}
                    <main className="col-span-1 lg:col-span-8 space-y-10">
                        {!lesson && !isGenerating && <EmptyState />}
                        {isGenerating && <GeneratingState />}
                        {lesson && (
                            <>
                                <LessonView lesson={lesson} />
                                <PronunciationPractice
                                    sentences={sentences}
                                    selectedSentence={selectedSentence}
                                    setSelectedSentence={setSelectedSentence}
                                    language={targetLanguage}
                                />
                            </>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div
            className="border border-dashed border-lexiBorder rounded-2xl p-10 bg-white/60 flex flex-col items-start gap-3"
            data-testid="poc-empty-state"
        >
            <BookOpenText className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Prêt·e à commencer.</h2>
            <p className="text-sm text-inkSoft max-w-md leading-relaxed">
                Utilisez le panneau à gauche pour générer votre première leçon. Vous pourrez
                ensuite écouter chaque mot et vous entraîner à la prononciation.
            </p>
        </div>
    );
}

function GeneratingState() {
    return (
        <div
            className="border border-lexiBorder rounded-2xl p-10 bg-white flex flex-col items-start gap-4 animate-fade-up"
            data-testid="poc-generating-state"
        >
            <Loader2 className="w-6 h-6 text-terracotta animate-spin" strokeWidth={1.5} />
            <div>
                <h2 className="font-serif text-2xl">Génération en cours…</h2>
                <p className="text-sm text-inkSoft mt-1">
                    Le modèle rédige votre leçon structurée. Cela prend une dizaine de secondes.
                </p>
            </div>
            <div className="w-full space-y-3 mt-2">
                {[...Array(3)].map((_, i) => (
                    <div
                        key={i}
                        className="h-16 rounded-xl bg-paperAlt animate-pulse-soft"
                        style={{ animationDelay: `${i * 120}ms` }}
                    />
                ))}
            </div>
        </div>
    );
}
